<script setup lang="ts">
import { DtStack } from '@dialpad/dialtone/vue3/lib/stack';
import { DtIcon } from '@dialpad/dialtone/vue3/lib/icon';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DtAvatar } from '@dialpad/dialtone/vue3/lib/avatar';
import { DtSkeleton } from '@dialpad/dialtone/vue3/lib/skeleton';
import {
  DtDropdown,
  DtDropdownSeparator,
} from '@dialpad/dialtone/vue3/lib/dropdown';
import { DtListItem } from '@dialpad/dialtone/vue3/lib/list-item';
import { DtNotice } from '@dialpad/dialtone/vue3/lib/notice';
import { useI18N } from '@dialpad/i18n';
import { useRouter, useRoute } from 'vue-router';
import { DATA_QA } from './navigation-side-bar.constants';
import {
  NAVIGATION_TABS,
  hostEnv,
  resetOverrides,
  OVERRIDES,
} from '../../../constants';
import logger from '@/logger';
import { onMounted } from 'vue';
import { useAuthController, useDPOAuth } from '@/auth';
import { useUser } from '@/use-user';

const log = logger.getChild('NavigationSideBar');
log.debug('Mounting');

onMounted(() => {
  log.debug('Mounted');
});

const { $t } = useI18N();

export type NavItem = {
  name: string;
  url: string;
};

const navItems: NavItem[] = [
  {
    name: NAVIGATION_TABS.CAMPAIGNS,
    url: '/campaigns',
  },
  { name: NAVIGATION_TABS.LISTS, url: '/lists' },
  { name: NAVIGATION_TABS.DNC, url: '/dnc' },
];

const router = useRouter();
const route = useRoute();

const authController = useAuthController();
const dpOAuth = useDPOAuth();

const signOut = async (): Promise<void> => {
  const authData = await authController.signOut();
  dpOAuth.redirectToSignout(authData);
};

const openFeedbackPage = (): void => {
  window.open(
    'https://docs.google.com/forms/d/e/1FAIpQLScc3Lao5mJHGeJkws9dI6oQGv5VCX0uBSDNXyOPF7wsiKT5Kw/viewform',
    '_blank',
  );
};

const openHelpPage = (): void => {
  window.open(
    'https://docs.google.com/document/d/1tFr9A-kdtXQuHn5rmOHhKpjzWdm1JVEiLIy_XP2_4iI/view?tab=t.0#heading=h.dfs7ipspyzrf',
    '_blank',
  );
};

const { me } = useUser();
</script>

<template>
  <div
    class="d-d-flex d-fd-column d-jc-space-between d-hmn100p d-hmx100p"
    :data-qa-loading="!me"
    :data-qa="DATA_QA.ROOT"
  >
    <DtStack key="menu" gap="300" as="ul" class="d-py16">
      <DtListItem
        v-for="item in navItems"
        :key="item"
        class="d-p8 d-pl16 d-bar0"
        navigation-type="tab"
        :data-qa="DATA_QA.ITEM"
        :class="{
          'd-list-item--highlighted': route.meta.tab === item.name,
        }"
        @click="router.push(item.url)"
      >
        <span
          :class="{ 'd-fw-semibold': route.meta.tab === item.name }"
          :data-qa="DATA_QA[item.name as keyof typeof DATA_QA]"
        >
          {{ $t(`SIDEBAR_${item.name}`) }}
        </span>
      </DtListItem>
    </DtStack>

    <div>
      <DtNotice
        v-if="Boolean(OVERRIDES)"
        title="Overrides active"
        kind="warning"
        :hide-close="true"
      >
        <template #action>
          <DtButton
            size="sm"
            importance="outlined"
            kind="muted"
            @click="resetOverrides()"
          >
            Clear
          </DtButton>
        </template>
      </DtNotice>
      <DtDropdown
        navigation-type="arrow-keys"
        placement="top-start"
        padding="large"
        class="d-mb16 d-ml8 d-mr8 d-wmn184 d-wmx100p"
      >
        <template #anchor>
          <DtButton
            importance="clear"
            kind="muted"
            :aria-label="$t('SIDEBAR_HELP_MENU_ARIA_LABEL')"
            :data-qa="DATA_QA.MENU"
            class="d-w100p d-bar-pill"
            label-class="d-of-hidden d-ws-nowrap"
          >
            <template #icon>
              <div>
                <DtAvatar
                  v-if="me"
                  size="sm"
                  :data-qa="DATA_QA.AVATAR"
                  :image-alt="me?.firstName"
                  :image-src="me?.imageUrl"
                ></DtAvatar>
                <DtSkeleton
                  v-else
                  :data-qa-loading="true"
                  :shape-option="{ size: 'sm', shape: 'circle' }"
                />
              </div>
            </template>
            <div class="d-w100p d-ml2 d-ta-left d-truncate">
              <span v-if="me" :data-qa="DATA_QA.NAME">
                {{ me?.firstName }}
              </span>
              <DtSkeleton v-else />
            </div>
          </DtButton>
        </template>
        <template #list="{ close }">
          <DtListItem
            role="menuitem"
            class="d-c-pointer d-w216"
            navigation-type="arrow-keys"
            :data-qa="DATA_QA.HELP"
            @click="openHelpPage()"
          >
            <span>{{ $t('SIDEBAR_HELP_MENU_ITEM_HELP') }}</span>
            <template #right>
              <DtIcon name="external-link" size="500" />
            </template>
          </DtListItem>
          <DtListItem
            role="menuitem"
            class="d-c-pointer"
            navigation-type="arrow-keys"
            :data-qa="DATA_QA.FEEDBACK"
            @click="openFeedbackPage()"
          >
            {{ $t('SIDEBAR_HELP_MENU_ITEM_FEEDBACK') }}
            <template #right>
              <DtIcon name="external-link" size="500" />
            </template>
          </DtListItem>
          <DtListItem
            role="menuitem"
            class="d-c-pointer"
            navigation-type="arrow-keys"
            :data-qa="DATA_QA.LOGOUT"
            @click="signOut()"
          >
            {{ $t('SIDEBAR_HELP_MENU_ITEM_LOGOUT') }}
          </DtListItem>
          <DtDropdownSeparator />
          <DtListItem
            role="menuitem"
            class="d-fc-muted"
            :data-qa="DATA_QA.VERSION"
            data-qa-mask="true"
            @click="close"
          >
            {{
              $t('SIDEBAR_HELP_MENU_ITEM_VERSION', {
                version: hostEnv.APP_VERSION,
              })
            }}
          </DtListItem>
        </template>
      </DtDropdown>
    </div>
  </div>
</template>
